/* eslint-disable no-magic-numbers */
import { useReactiveVar } from '@apollo/client';
import { Typography, useTheme } from '@mui/material';
import Drawer from 'components/Drawer/Drawer';
import Modal from 'components/Modal/Modal';
import { Door, Location } from 'graphql/generated/graphqlTypes';
import useBatteryStatusUI from 'hooks/useBatteryStatusUI/useBatteryStatusUI';
import useDoorsAndLocations from 'hooks/useDoorsAndLocations/useDoorsAndLocations';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useSortingLocks from 'hooks/useSorting/useSortingLocks/useSortingLocks';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { kebabCase, sortBy } from 'lodash';
import React, { useState } from 'react';
import { authVar } from 'state/vars';
import styled from 'styled-components';
import AvatarGroupCell from 'ui/atoms/AvatarGroupCell/AvatarGroupCell';
import BatteryStatusCell from 'ui/atoms/BatteryStatusCell/BatteryStatusCell';
import Button from 'ui/atoms/Button/Button';
import Icon, { Icons } from 'ui/atoms/Icon/Icon';
import TableCell from 'ui/atoms/TableCell/TableCell';
import TableHeader from 'ui/atoms/TableHeader/TableHeader';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import Search from 'ui/molecules/Search/SearchMUI';
import TableHeaderRow from 'ui/molecules/TableHeaderRow/TableHeaderRow';
import TableRow from 'ui/molecules/TableRow/TableRow';
import BatteryStatus from 'ui/organisms/BatteryStatus/BatteryStatus';
import Table from 'ui/organisms/Table/Table';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import PageContent from 'ui/templates/PageContent/PageContent';
import { StickyToTop } from 'ui/templates/StickyToTop/StickyToTop';
import ArrayUtil from 'utils/Array/Array.util';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import getLockIconProperties from 'utils/LocksIcon/LocksIcon.util';
import { createHardwareTabs } from '../hardwareTabsConst';

const TableWrapper = styled.div`
  height: calc(100vh - 24rem);
  width: calc(100vw - 25rem);
  margin-right: 1rem;
`;

export const createColumns = (translation: Translation) => [
  { text: translation.name, width: 15 },
  { text: translation.group, width: 15 },
  { text: translation.office_mode, width: 15 },
  { text: translation.serial_number, width: 15 },
  { text: translation.battery_status, width: 20 }
];

const Locks: React.FC = () => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const columns = createColumns(translation);
  const hardwareTabs = createHardwareTabs(translation);

  const [filterStr, setFilterStr] = useState('');
  const { showDrawer } = useDrawer();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { doorsWithLocationsLoading, isOfficeMode, locationsWithDoorsLoading } = useDoorsAndLocations({
    handleFetchError
  });
  const { getBatteryData } = useBatteryStatusUI(translation);
  const skeletonArray = ArrayUtil.SkeletonArray();
  const { handleSortingOnClick, handleSortingIcon, getSortedArray } = useSortingLocks(translation, theme);

  const handleRowClick = (door: Door) => {
    if (!doorsWithLocationsLoading && !locationsWithDoorsLoading) {
      const contentValue = {
        name: door.name || '',
        hardwareIdentifier: door.hardwareIdentifier,
        doorId: door.id || '',
        isOfficeModeEnabled: isOfficeMode(door),
        officeModeFromValue:
          door.officeMode.weekDays.length > 0 ? door.officeMode.weekDays[0].from.slice(0, -3) : undefined,
        officeModeToValue:
          door.officeMode.weekDays.length > 0 ? door.officeMode.weekDays[0].to.slice(0, -3) : undefined,
        batteryWarningLevel: door.batteryWarningLevel,
        groupIds: (sortBy(door.locations, (location) => location.name) as unknown as Location[]) || [],
        type: door.type
      };
      showDrawer({
        type: 'editLock',
        contentValue: contentValue
      });
    }
  };

  const handleManageLockGroupsClick = () => {
    if (!doorsWithLocationsLoading)
      showDrawer({
        type: 'manageLockGroups'
      });
  };

  const getOfficeModeTime = (door: Door): string =>
    `${door.officeMode.weekDays[0].from.slice(0, -3)} - ${door.officeMode.weekDays[0].to.slice(0, -3)}`;

  const getFilterData = (): Door[] =>
    filterBySearchTerm(getSortedArray(), filterStr, (element) => [
      element.name,
      ...(element.locations?.map((group) => group.name) || []),
      element.hardwareIdentifier,
      getBatteryData(element.batteryWarningLevel, theme).firstLineText,
      isOfficeMode(element) ? getOfficeModeTime(element) : '-'
    ]);

  const handleClearButtonOnClick = () => {
    setFilterStr('');
  };

  return (
    <PageContent title="ATLAS" titleId="location" tabs={hardwareTabs} id="locks-page">
      <StickyToTop>
        <ComponentWrapper
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          gap="0.5rem"
          flexDirection="row"
        >
          <Search
            style={{ width: '15rem' }}
            onChange={(event) => setFilterStr(event.target.value)}
            value={filterStr}
            clearButtonOnClick={handleClearButtonOnClick}
          />
          <Button
            style={{ width: 'fit-content', padding: '0 16px' }}
            onClick={handleManageLockGroupsClick}
            id="manage-lock-groups-button"
          >
            {translation.manage_lock_groups}
          </Button>
        </ComponentWrapper>
      </StickyToTop>
      <TableWrapper>
        <Table
          header={
            <TableHeaderRow placeForAvatarOrSwitchOrCheckbox placeForArrow>
              {columns.map((column, index) =>
                index < 5 ? (
                  <TableHeader
                    headerText={column.text}
                    id={kebabCase(`header-${column.text}`)}
                    flex={`0 0 ${column.width}%`}
                    iconSorting={handleSortingIcon(column.text)}
                    onClick={column.text !== 'Group' ? () => handleSortingOnClick(column.text) : undefined}
                    key={column.text}
                  />
                ) : (
                  <ComponentWrapper flex={`0 0 ${column.width}%`} gap="4px" alignItems="center">
                    <TableHeader
                      headerText={column.text}
                      id={kebabCase(`header-${column.text}`)}
                      iconSorting={handleSortingIcon(column.text)}
                      onClick={column.text !== 'Group' ? () => handleSortingOnClick(column.text) : undefined}
                      key={column.text}
                    />
                    <Tooltip elementOnHover={<BatteryStatus />} id="tooltip">
                      <ComponentWrapper cursor="pointer">
                        <Icon name="Info" id="i-icon" />
                      </ComponentWrapper>
                    </Tooltip>
                  </ComponentWrapper>
                )
              )}
            </TableHeaderRow>
          }
          headerId="lock-headers"
          withScroll
          rowsWrapperId="lock-list"
        >
          {doorsWithLocationsLoading
            ? skeletonArray.map((_, index) => (
                <TableRow
                  beforeContentIconProps={{
                    name: '' as Icons,
                    height: 28,
                    width: 28,
                    viewBox: '0 0 32 32'
                  }}
                  id={`row-skeleton-${index}`}
                  marginAfterContent
                  key={`skeletonTableRow-${_.id}`}
                >
                  {columns.map((column) => (
                    <TableCell isLoading firstLineText="" flex={`0 0 ${column.width}%`} />
                  ))}
                </TableRow>
              ))
            : getFilterData().map((door, id) => (
                <TableRow
                  onClick={() => handleRowClick(door)}
                  beforeContentIconProps={{
                    ...getLockIconProperties.getLockIconProperties(door.type || null),
                    height: 28,
                    width: 28,
                    viewBox: '0 0 32 32'
                  }}
                  beforeContentIconTooltip={
                    <ComponentWrapper padding="8px">
                      <Typography color="textPrimary">
                        {getLockIconProperties.getLockTooltipText(door.type || null)}
                      </Typography>
                    </ComponentWrapper>
                  }
                  beforeContentIconTooltipPosition={
                    getFilterData().length === id + 1 ? { left: '0', bottom: '0' } : { top: '0', left: '0' }
                  }
                  id={`row-${id}`}
                  hoverEffect
                  hoverArrow
                  key={door.id}
                >
                  <TableCell
                    firstLineText={door.name}
                    firstLineId={`row-${id}-name`}
                    flex={`0 0 ${columns[0].width}%`}
                  />
                  {door.locations && door.locations.length > 0 ? (
                    <AvatarGroupCell
                      avatarGroupProps={{
                        avatars: sortBy(door.locations, (location) => location.name)
                          .reverse()
                          .map((group) => ({
                            text: group.name,
                            variant: 'small',
                            backgroundColor: 'b4',
                            textColor: 'lTextHigh',
                            id: group.id
                          })),
                        tooltipPosition: getFilterData().length === id + 1 ? 'top' : 'bottom',
                        id: 'row-groups-avatar'
                      }}
                      flex={`0 0 ${columns[1].width}%`}
                    />
                  ) : (
                    <TableCell
                      firstLineText="-"
                      firstLineId={`row-${id}-no-groups`}
                      flex={`0 0 ${columns[1].width}%`}
                    />
                  )}

                  <TableCell
                    firstLineText={isOfficeMode(door) ? getOfficeModeTime(door) : '-'}
                    firstLineId={`row-${id}-office-mode`}
                    firstLineColor="lTextHigh"
                    flex={`0 0 ${columns[2].width}%`}
                  />
                  <TableCell
                    firstLineText={door.hardwareIdentifier}
                    firstLineId={`row-${id}-serial-number`}
                    flex={`0 0 ${columns[3].width}%`}
                  />
                  <BatteryStatusCell
                    id={`row-${id}-battery-status`}
                    status={door.batteryWarningLevel}
                    flex={`0 0 ${columns[4].width}%`}
                  />
                </TableRow>
              ))}
        </Table>
      </TableWrapper>

      <Drawer />
      <Modal />
    </PageContent>
  );
};

export default Locks;
